/**
 * BlogToc — auto-generated table of contents for blog articles.
 *
 * - Scans a target content element (default: `.blog-post-content`) for headings
 *   matching `data-headings` (default: `h2, h3`).
 * - Assigns slug-based ids to headings (preserving any pre-existing id).
 * - Renders a list of anchor links into the element's `[data-toc-list]` slot,
 *   tagging each link with `data-toc-level` so deeper levels can be indented.
 * - Highlights the active heading on scroll using IntersectionObserver.
 * - On click: smooth-scrolls to the target and updates the URL hash via history.pushState.
 * - Hides itself if the target has fewer matching headings than `data-min-headings`.
 */
class BlogToc extends HTMLElement {
  #headings = [];
  /** @type {Map<string, HTMLAnchorElement[]>} multiple lists (mobile + desktop) share the same ids */
  #links = new Map();
  #activeId = null;
  #suppressScroll = false;
  #suppressTimer = null;
  #scrollHandler = null;
  #scrollRaf = 0;

  connectedCallback() {
    const targetSelector = this.getAttribute('data-target') || '.blog-post-content';
    const headingSelector = this.getAttribute('data-headings') || 'h2, h3';
    const minHeadings = parseInt(this.getAttribute('data-min-headings') || '2', 10);
    const target = document.querySelector(targetSelector);

    if (!target) {
      this.#hide();
      return;
    }

    this.#headings = Array.from(target.querySelectorAll(headingSelector));
    if (this.#headings.length < minHeadings) {
      this.#hide();
      return;
    }

    this.#assignIds();
    this.#render();
    this.#wireClicks();
    this.#observe();
    this.#scrollToInitialHash();
  }

  disconnectedCallback() {
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
    if (this.#scrollRaf) {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = 0;
    }
    if (this.#suppressTimer) {
      clearTimeout(this.#suppressTimer);
      this.#suppressTimer = null;
    }
  }

  #hide() {
    this.hidden = true;
    this.setAttribute('data-empty', '');
  }

  #slugify(text) {
    const base = text
      .toLowerCase()
      .trim()
      .replace(/[^\p{L}\p{N}\s-]/gu, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return base || 'section';
  }

  #assignIds() {
    const used = new Set();
    document.querySelectorAll('[id]').forEach((el) => used.add(el.id));

    for (const heading of this.#headings) {
      if (heading.id && !used.has(`__dup_${heading.id}`)) {
        used.add(heading.id);
        continue;
      }
      let slug = this.#slugify(heading.textContent || '');
      let candidate = slug;
      let i = 2;
      while (used.has(candidate)) {
        candidate = `${slug}-${i++}`;
      }
      heading.id = candidate;
      used.add(candidate);
    }
  }

  #render() {
    const lists = this.querySelectorAll('[data-toc-list]');
    if (!lists.length) return;

    for (const list of lists) {
      const fragment = document.createDocumentFragment();
      for (const heading of this.#headings) {
        const level = parseInt(heading.tagName.slice(1), 10) || 3;
        const item = document.createElement('li');
        item.className = 'ako-blog-toc__item';
        item.dataset.tocLevel = String(level);

        const link = document.createElement('a');
        link.className = 'ako-blog-toc__link';
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent || '';
        link.dataset.tocTarget = heading.id;
        link.dataset.tocLevel = String(level);

        item.appendChild(link);
        fragment.appendChild(item);

        const existing = this.#links.get(heading.id);
        if (existing) existing.push(link);
        else this.#links.set(heading.id, [link]);
      }
      list.replaceChildren(fragment);
    }
  }

  #wireClicks() {
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-toc-target]');
      if (!link) return;
      event.preventDefault();
      const id = link.dataset.tocTarget;
      const heading = document.getElementById(id);
      if (!heading) return;

      this.#lockActive(id);
      history.pushState(null, '', `#${id}`);
      heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /**
   * Lock the active highlight to a heading and suppress observer-driven updates
   * until smooth-scroll settles. Releases on `scrollend`, or after a fallback
   * timeout for browsers without `scrollend` support.
   */
  #lockActive(id) {
    this.#suppressScroll = true;
    this.#setActive(id);

    if (this.#suppressTimer) clearTimeout(this.#suppressTimer);

    const release = () => {
      this.#suppressScroll = false;
      window.removeEventListener('scrollend', release);
      if (this.#suppressTimer) {
        clearTimeout(this.#suppressTimer);
        this.#suppressTimer = null;
      }
    };

    window.addEventListener('scrollend', release, { once: true });
    this.#suppressTimer = setTimeout(release, 1000);
  }

  /**
   * Track the active heading via scroll position rather than IntersectionObserver
   * entries, which can flip the active state when two adjacent headings straddle
   * the active zone. Computing the active from all headings each frame yields a
   * stable single-source-of-truth and eliminates flicker.
   */
  #observe() {
    this.#scrollHandler = () => {
      if (this.#scrollRaf) return;
      this.#scrollRaf = requestAnimationFrame(() => {
        this.#scrollRaf = 0;
        if (this.#suppressScroll) return;
        this.#updateActiveFromScroll();
      });
    };
    window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    this.#updateActiveFromScroll();
  }

  #headerOffset() {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed + 24 : 100;
  }

  #updateActiveFromScroll() {
    const threshold = this.#headerOffset();
    let activeId = this.#headings[0]?.id ?? null;
    for (const heading of this.#headings) {
      if (heading.getBoundingClientRect().top - threshold <= 0) {
        activeId = heading.id;
      } else {
        break;
      }
    }
    if (activeId) this.#setActive(activeId);
  }

  #setActive(id) {
    if (id === this.#activeId) return;
    if (this.#activeId) {
      this.#links.get(this.#activeId)?.forEach((link) => link.removeAttribute('aria-current'));
    }
    const links = this.#links.get(id);
    if (links?.length) {
      links.forEach((link) => link.setAttribute('aria-current', 'true'));
      this.#activeId = id;
    }
  }

  #scrollToInitialHash() {
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    const heading = document.getElementById(hash);
    if (!heading || !this.#headings.includes(heading)) return;
    requestAnimationFrame(() => {
      heading.scrollIntoView({ behavior: 'auto', block: 'start' });
      this.#setActive(hash);
    });
  }
}

if (!customElements.get('ako-blog-toc')) {
  customElements.define('ako-blog-toc', BlogToc);
}
